import React from 'react';
import ListGrid from '../../components/ListGrid';
import { isUncounted, qtyBoxValue, OwnerChip } from './lineState';
import { pressable } from '../../lib/pressable';

/*
  The lines on a count sheet. One list, at every width.

  Written 5 September 2026 for job 10, which is named after this: "Count entry
  becomes one screen."

  **There were three of them and they had drifted.** A table on a desk, a
  narrower table on a tablet, and a stack of cards on a phone, each written out
  in full inside `CountEntry.jsx`, each roughly 130 lines, and each with its own
  copy of the row menu and the quantity stepper. What that cost, found by
  reading the three side by side:

  - **The vendor was on a desk and on a tablet and nowhere on a phone**, so a rep
    holding a phone in a stock room could not tell two identical descriptions
    apart by who supplies them.
  - **A note was shown under the description on a desk and a tablet, and only as
    the word "Note" on a phone**, so the actual words a rep had written for the
    administrator were invisible on the thing they were most likely to be
    holding.
  - **The row menu existed three times.** When "Edit this line" was added, it had
    to be added three times, and the version below 1024 opened nothing because
    the dialog was never rendered in that tree. That is exactly the fault this
    job exists to stop.

  Now there is one definition of a row and the stylesheet decides its shape:
  columns on a desk, fewer columns in the middle, a block on a phone. Rule 15.
*/

// The tint a row carries, from the three flags a line can have, plus rule 11's
// state bar saying whether anybody has counted it.
export function rowToneOf(item) {
  if (item?.is_dead_stock)    return 'tone-dead';
  if (item?.is_non_inventory) return 'tone-noninv';
  if (item?.not_in_catalog)   return 'tone-new';
  return '';
}

export function rowClassOf(item) {
  return (rowToneOf(item) + ' ' + (isUncounted(item) ? 'state-blank' : 'state-counted')).trim();
}

// The small uppercase chips on a line: what it is, rather than what state it is
// in. The state is the bar down the left edge.
export function Flags({ item }) {
  const chips = [];
  if (item.not_in_catalog)   chips.push(['Not in catalog', 'var(--warning-ink)', 'var(--warning-bg)']);
  if (item.is_non_inventory) chips.push(['Non-inv',        'var(--info-ink)', 'var(--info-bg)']);
  if (item.is_dead_stock)    chips.push(['Dead stock',     'var(--text-secondary)', 'var(--hover)']);
  if (chips.length === 0) return null;
  return (
    <span style={{ display: 'inline-flex', gap: 4, flexWrap: 'wrap', marginLeft: 6 }}>
      {chips.map(([label, fg, bg]) => (
        <span key={label} style={{ fontSize: 9, fontWeight: 800, color: fg, background: bg, borderRadius: 4, padding: '1px 5px', letterSpacing: 0.5, textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
          {label}
        </span>
      ))}
    </span>
  );
}

/*
  The quantity stepper: minus, the box, plus.

  **The minus is unavailable on a line nobody has counted**, because one less
  than nothing is not a count, and pressing it used to write a 0 and turn a line
  somebody still owed into a line counted as empty.
*/
/*
  **The size comes from the stylesheet now, not from a number typed here.**
  Corrected 7 September 2026, job 18, and it was not a judgement call: the
  approved drawings say what these are and the app never built them.

  | Approved drawing | The plus and minus | What the app had |
  |---|---|---|
  | `desktop.html` | 28 wide, 34 tall | 30 x 36 |
  | `tablet.html`  | 46 wide, 46 tall | 30 x 36 |
  | `phone.html`   | 44 wide, 44 tall | 30 x 36 |

  So on a phone and a tablet **the most-tapped things in the whole app have
  never met the 44 pixel floor**, which is the one control rule 13 names by
  name. The `tall` prop is gone: it had two values, only one call site, and that
  site always passed it, so the 34 branch had never run.
*/
/*
  **Rebrand step 5, 17 September 2026 (guide 12.3).** On a computer the box
  stands alone, 92 by 40 and right aligned, because counting there is by
  keyboard (`steppers={false}`); on a phone the plus and minus are the large
  48 pixel ones (`.qty-stepper.is-big`). Every one of them still calls the same
  `onChange`, which is the one save. `invalid` shows the red edge and the
  jostle after a refused key.
*/
export function QtyStepper({ item, inputRef, onFocus, onBlur, onKeyDown, onChange, saved = false, steppers = true, big = false, invalid = false, label }) {
  const blank = isUncounted(item);
  return (
    <span className="qty-cell-wrap">
    <span className={'qty-stepper' + (saved ? ' is-saved' : '') + (steppers ? '' : ' is-bare') + (big ? ' is-big' : '')}>
      {steppers && (
        <button type="button" className="qty-step" disabled={blank}
          aria-label={'One less' + (label ? ', ' + label : '')}
          onClick={() => onChange((item.quantity || 0) - 1)}
          style={{ cursor: blank ? 'not-allowed' : 'pointer', opacity: blank ? 0.4 : 1 }}>-</button>
      )}
      <input type="number" min="0" ref={inputRef} value={qtyBoxValue(item)} placeholder="?"
        className={'qty-box' + (blank ? ' blank' : '')}
        aria-label={'Count' + (label ? ' for ' + label : '')}
        aria-invalid={invalid ? 'true' : undefined}
        inputMode="numeric"
        onFocus={onFocus} onBlur={onBlur} onKeyDown={onKeyDown}
        onChange={e => onChange(e.target.value)} />
      {steppers && (
        <button type="button" className="qty-step"
          aria-label={'One more' + (label ? ', ' + label : '')}
          onClick={() => onChange((item.quantity ?? 0) + 1)}>+</button>
      )}
    </span>
    {/* The check pop's word, rebrand step 3 (guide 10.5). */}
    {saved && (
      <span className="qty-saved" aria-hidden="true">
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12.5l4.5 4.5L19 7.5" /></svg>
        Saved
      </span>
    )}
    </span>
  );
}

/* Counted or not, in words (guide 11.4: colour is never the only signal). */
export function LineStatus({ item }) {
  const blank = isUncounted(item);
  return <span className={'pill ' + (blank ? 'pill-ns' : 'pill-app')}>{blank ? 'Not counted' : 'Counted'}</span>;
}

/*
  The row menu, once.

  Five things, in the order the approved dialogs and the drawings put them:
  edit the line, its note, its two flags, and taking it off the sheet last
  because it is the one that removes work.
*/
export function RowMenu({ item, open, onToggle, actions }) {
  const [pos, setPos] = React.useState(null);
  const entries = [
    { label: 'Edit this line', run: () => actions.edit(item) },
    { label: item.notes ? 'Edit the note' : 'Add a note', run: () => actions.note(item), lit: !!item.notes, fg: 'var(--info-ink)', bg: 'var(--info-bg)' },
    { label: item.is_non_inventory ? 'Not non-inventory' : 'Mark non-inventory', run: () => actions.nonInventory(item), lit: item.is_non_inventory, fg: 'var(--info-ink)', bg: 'var(--info-bg)' },
    { label: item.is_dead_stock ? 'Not dead stock' : 'Mark dead stock', run: () => actions.deadStock(item), lit: item.is_dead_stock, fg: 'var(--text-secondary)', bg: 'var(--hover)' },
    { label: 'Take off the sheet', run: () => actions.remove(item), fg: 'var(--critical-ink)', bg: 'var(--critical-bg)', always: true },
  ];

  /*
    **The menu is pinned to the window, not to the row, and it flips up when
    there is no room below.**

    A menu positioned inside the list is cut off by the list's own scrolling:
    on the last row on screen it ran 190 pixels past the bottom of a laptop, and
    on a phone it did not appear at all. Found by opening it on the last row in
    a real browser, which is not something a photograph of the list would ever
    show, because a photograph has no menu open.
  */
  const HEIGHT = 5 * 42 + 8;
  function toggle(e) {
    e.stopPropagation();
    if (open) { onToggle(null); return; }
    const r = e.currentTarget.getBoundingClientRect();
    const roomBelow = window.innerHeight - r.bottom;
    setPos(roomBelow > HEIGHT
      ? { top: r.bottom + 4, right: Math.max(8, window.innerWidth - r.right) }
      : { bottom: window.innerHeight - r.top + 4, right: Math.max(8, window.innerWidth - r.right) });
    onToggle(item.id);
  }

  return (
    <span style={{ position: 'relative', display: 'inline-block' }}>
      <button type="button" aria-label="More for this line" onClick={toggle} className="line-more"
        style={{ borderRadius: 'var(--oh-radius-chip)', border: 'none', boxShadow: 'inset 0 0 0 1px var(--border)', background: open ? 'var(--selected)' : 'var(--surface-glass)', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 2, padding: 0 }}>
        {[0, 1, 2].map(i => (
          <span key={i} style={{ width: 3, height: 3, borderRadius: '50%', background: open ? 'var(--action)' : 'var(--text-secondary)', display: 'block' }} />
        ))}
      </button>
      {open && pos && (
        <span onClick={e => e.stopPropagation()} className="oh-menu-glass"
          style={{ position: 'fixed', ...pos, zIndex: 9999, minWidth: 190, overflow: 'hidden', display: 'block', textAlign: 'left', padding: 6 }}>
          {entries.map((e, i) => (
            <button key={e.label} type="button" onClick={() => { e.run(); onToggle(null); }}
              style={{ width: '100%', minHeight: 40, padding: '10px 12px', borderRadius: 'var(--oh-radius-control)', background: (e.lit || e.always) ? e.bg : 'transparent', border: 'none', marginTop: i === 0 ? 0 : 2, fontSize: 14, fontWeight: 600, color: (e.lit || e.always) ? e.fg : 'var(--text-primary)', cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left', display: 'block' }}>
              {e.label}
            </button>
          ))}
        </span>
      )}
    </span>
  );
}

/*
  Where a line sits on a shelf, editable in place. It was three separate
  spellings of the same thing, and the phone's could not be typed into at all
  without the row jumping.
*/
export function LocationCell({ item, editing, draft, setDraft, onOpen, onSave, onCancel }) {
  if (editing) {
    return (
      <span style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
        <input autoFocus value={draft} onChange={e => setDraft(e.target.value.toUpperCase())}
          onKeyDown={e => { if (e.key === 'Enter') onSave(); if (e.key === 'Escape') onCancel(); }}
          style={{ width: 84, padding: '4px 8px', fontSize: 12, border: '1.5px solid var(--action)', borderRadius: 'var(--oh-radius-chip)', fontFamily: 'inherit', outline: 'none', minWidth: 0 }} />
        <button type="button" onClick={onSave}
          style={{ fontSize: 11, fontWeight: 700, padding: '5px 9px', borderRadius: 'var(--oh-radius-chip)', border: 'none', background: 'var(--action)', color: 'var(--on-action)', cursor: 'pointer', fontFamily: 'inherit' }}>Save</button>
      </span>
    );
  }
  return (
    <span {...pressable(() => onOpen(item))} title="Press to set where this sits"
      style={{ cursor: 'pointer', fontSize: 11, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', color: item.location ? 'var(--info-ink)' : 'var(--text-secondary)', background: item.location ? 'var(--info-bg)' : 'transparent', borderRadius: 4, padding: item.location ? '2px 7px' : 0, fontStyle: item.location ? 'normal' : 'italic' }}>
      {item.location || 'Add...'}
    </span>
  );
}

/*
  **Rebrand step 5, 17 September 2026 (guide 12.3).** Two shapes, as before,
  and the stylesheet still shows whichever fits:

  - **the type-in grid on a computer**: Item, Item number, Shelf (when Item
    Locations is on), Vendor, Status, Count, and the row menu. No plus and
    minus: counting there is by keyboard. The row you are in is lit, and a row
    the quick entry bar has just saved flashes.
  - **the stepper list on a phone**: the item's name first, its number and
    vendor under it, where it sits, and a large stepper with the row menu.

  The tablet has its own split (`SplitView.jsx`) and does not use this.
*/
export default function LineList({
  items, myId, shared, locationEnabled, sort, onSort,
  openMenu, onToggleMenu, actions,
  qtyRef, onQtyFocus, onQtyBlur, onQtyKey, onQty, savedId,
  activeId, flashId, invalidId,
  editingLocation, locationDraft, setLocationDraft, onLocationOpen, onLocationSave, onLocationCancel,
  empty,
}) {
  const columns = [
    { label: 'Item',        width: '2.6fr', sortKey: 'description_raw' },
    { label: 'Item number', width: '1.3fr', sortKey: 'item_number_raw' },
    ...(locationEnabled ? [{ label: 'Shelf', width: '0.9fr', sortKey: 'location' }] : []),
    { label: 'Vendor',      width: '1.2fr', sortKey: 'vendor_raw' },
    { label: 'Status',      width: '124px' },
    { label: 'Count',       width: '104px', align: 'right' },
    { label: '',            width: '48px',  align: 'right' },
  ];

  /*
    **Every row is in the page twice**, once as columns and once as the block a
    narrow screen shows, and the stylesheet hides whichever does not apply. That
    is how the shared list has always worked and it is what lets one definition
    draw two shapes. It has one consequence worth knowing: **there are two
    quantity boxes per line**, so the keyboard has to be handed both and focus
    whichever is actually on screen. Found by pressing Enter in a real browser,
    where it moved the cursor into the invisible copy and looked like the key
    doing nothing at all.
  */
  const qty = (item, where) => (
    <QtyStepper item={item}
      inputRef={el => qtyRef(item.id, where, el)}
      onFocus={e => onQtyFocus(item.id, e)}
      onBlur={() => onQtyBlur(item.id)}
      onKeyDown={e => onQtyKey(e, item)}
      onChange={v => onQty(item.id, v)}
      saved={savedId === item.id}
      invalid={invalidId === item.id}
      steppers={where !== 'wide'}
      big={where === 'narrow'}
      label={item.description_raw || item.item_number_raw} />
  );

  const menu = item => (
    <RowMenu item={item} open={openMenu === item.id} onToggle={onToggleMenu} actions={actions} />
  );

  const where = item => (
    <LocationCell item={item} editing={editingLocation === item.id} draft={locationDraft}
      setDraft={setLocationDraft} onOpen={onLocationOpen}
      onSave={() => onLocationSave(item)} onCancel={onLocationCancel} />
  );

  const note = item => item.notes
    ? <span className="ce-note">Note: {item.notes}</span>
    : null;

  return (
    <ListGrid
      stickyHead
      columns={columns}
      rows={items}
      keyOf={i => i.id}
      rowClass={item => rowClassOf(item)
        + (activeId === item.id ? ' is-on' : '')
        + (flashId === item.id ? ' is-flash' : '')}
      sort={sort}
      onSort={onSort}
      empty={empty}
      cells={item => [
        <span style={{ minWidth: 0 }}>
          <span className="ce-name">
            {item.description_raw}
            {shared && <OwnerChip item={item} myId={myId} />}
          </span>
          {note(item)}
        </span>,
        <span className="ce-itemno" style={{ color: item.not_in_catalog ? 'var(--warning-ink)' : 'var(--action-text)' }}>
          {item.item_number_raw}<Flags item={item} />
        </span>,
        ...(locationEnabled ? [where(item)] : []),
        <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{item.vendor_raw || '--'}</span>,
        <LineStatus item={item} />,
        qty(item, 'wide'),
        menu(item),
      ]}
      stack={item => (
        <span className="ce-card">
          <span className="ce-card-text">
            <span className="ce-name">
              {item.description_raw}
              {shared && <OwnerChip item={item} myId={myId} />}
            </span>
            <span className="ce-card-meta">
              <span className="ce-itemno" style={{ color: item.not_in_catalog ? 'var(--warning-ink)' : 'var(--action-text)' }}>{item.item_number_raw}</span>
              <span>{item.vendor_raw || '--'}</span>
              <Flags item={item} />
            </span>
            {note(item)}
          </span>
          <span className="ce-card-foot">
            {locationEnabled ? <span className="ce-card-shelf">{where(item)}</span> : <span />}
            <span className="ce-card-actions">
              {qty(item, 'narrow')}
              {menu(item)}
            </span>
          </span>
        </span>
      )}
    />
  );
}
